import { ApolloClient, gql } from 'apollo-boost';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';
import { removeConnectionsMemoized } from '..';
import { usePublicServices } from '../../providers/PublicServicesContext';
import {
  RequestState,
  requestError,
  requestStart,
  requestSuccess,
  setErrorResponse,
} from '../../auth/hooks/useRequestState';

import {
  INDICATOR_STAGES_KEY,
  IndicatorSectionKey,
  Service,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';

const query = gql`
  query allServiceCollections($donorId: Int, $portfolioId: Int) {
    allServiceCollections(
      filters: { donorId: $donorId, portfolioId: $portfolioId }
    ) {
      edges {
        node {
          dbId
          name
          description
          version
          services {
            edges {
              node {
                dbId
                name
                description
                goal
                version
                indicators {
                  edges {
                    node {
                      dbId
                      id
                      name
                      stageId
                      customizable
                      unit
                      definitionStart
                      definitionEnd
                      initialForecastStart
                      initialForecastEnd
                      initialMeasurementStart
                      initialMeasurementEnd
                      outcome
                      impactReceiptText
                      forecastOutcome
                      finalOutcome
                      finalForecastStart
                      finalForecastEnd
                      isHuman
                      finalMeasurementStart
                      finalMeasurementEnd
                      definition
                      stgName
                      version
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

type ServiceIndicator = Service['indicators'][number] & { dbId?: number };

export const getServiceIndicators = (
  mainServiceGoal: string | undefined,
  serviceGoal: string | undefined,
  services: ServiceCollection[],
): { [K in IndicatorSectionKey]: ServiceIndicator[] } => {
  const result: {
    [x in IndicatorSectionKey]: ServiceIndicator[];
  } = {
    capacityDevelopment: [],
    reach: [],
    succeed: [],
    catalyzed: [],
    learn: [],
    act: [],
  };
  if (!mainServiceGoal || !serviceGoal) return result;
  const mainService = services.find(x => x.name === mainServiceGoal);
  const service = mainService?.services.find(x => x.name === serviceGoal);
  if (!service) return result;
  for (const indicator of service.indicators) {
    result[INDICATOR_STAGES_KEY[indicator.stageId]]?.push(indicator);
  }
  return result;
};

export interface ServiceCollection {
  dbId: number;
  name: string;
  description: string;
  version: number;
  services: Service[];
}

export interface ServiceByIds {
  [serviceId: string]:
    | { [subServiceId: string]: ServiceIndicator[] | undefined }
    | undefined;
}

interface ServiceCollectionsProps {
  reportId?: number | string;
  portfolioId?: number;
  donorId?: number;
}

export const useAllServiceCollections = ({
  reportId,
  portfolioId,
  donorId,
}: ServiceCollectionsProps = {}): {
  data: ServiceCollection[];
  refetch: () => Promise<unknown>;
  loading: boolean;
} => {
  // Check if we have public services from context (for public report viewing)
  const publicServicesContext = usePublicServices();

  const shouldSkip = !reportId || publicServicesContext.services.length > 0;

  const { data: rawData, refetch, loading } = useQuery(query, {
    variables: {
      reportId,
      portfolioId,
      donorId,
    },
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  // If we have public services, use them instead of query results
  if (publicServicesContext.services.length > 0) {
    // Transform public services to match ServiceCollection format
    const publicData = publicServicesContext.services.map(service => ({
      dbId: service.dbId,
      name: service.name,
      description: service.description,
      version: service.version,
      services: [
        {
          dbId: service.dbId,
          name: service.name,
          description: service.description,
          goal: service.goal,
          indicators: service.indicators,
        },
      ],
    }));
    return {
      data: (publicData as unknown) as ServiceCollection[],
      refetch: () => Promise.resolve(),
      loading: publicServicesContext.loading,
    };
  }

  const data =
    rawData?.allServiceCollections?.edges.length > 0
      ? removeConnectionsMemoized(rawData?.allServiceCollections)
      : [];

  return {
    data,
    refetch,
    loading,
  };
};

export const getServiceCollections = (
  client: ApolloClient<object>,
  reportId: number | string,
) => {
  return client.query<{ allServiceCollections: ServiceCollection[] }>({
    query: query,
    variables: {
      reportId,
    },
    fetchPolicy: 'network-only',
  });
};

export const useServiceCollections = ({
  reportId,
  portfolioId,
  donorId,
}: ServiceCollectionsProps = {}): RequestState<ServiceCollection[]> => {
  const publicServicesContext = usePublicServices();
  const hasPublicServices = publicServicesContext.services.length > 0;

  const { data, loading, error } = useQuery(query, {
    variables: { reportId, portfolioId, donorId },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    skip: hasPublicServices,
  });

  return useMemo<RequestState<ServiceCollection[]>>(() => {
    if (hasPublicServices) {
      if (publicServicesContext.loading) return requestStart();
      const publicData = publicServicesContext.services.map(service => ({
        dbId: service.dbId,
        name: service.name,
        description: service.description,
        version: service.version,
        services: [
          {
            dbId: service.dbId,
            name: service.name,
            description: service.description,
            goal: service.goal,
            indicators: service.indicators,
          },
        ],
      }));
      return requestSuccess<ServiceCollection[]>(
        (publicData as unknown) as ServiceCollection[],
      );
    }

    if (loading && !data) return requestStart();

    if (error) {
      return requestError(
        setErrorResponse({
          status: 500,
          message: error.message || 'Failed to load service collections.',
          isAxiosError: false,
        }),
      );
    }

    const list =
      data?.allServiceCollections?.edges?.length > 0
        ? (removeConnectionsMemoized(
            data.allServiceCollections,
          ) as ServiceCollection[])
        : [];
    return requestSuccess<ServiceCollection[]>(list);
  }, [
    hasPublicServices,
    publicServicesContext.loading,
    publicServicesContext.services,
    loading,
    error,
    data,
  ]);
};
